// @flow
import React from 'react';
import _ from 'lodash';

type Params = {
  formatType?: 'bracket' | 'indexed' | 'comma';
};

export const checkAuth = (store: Object) => (nextState: any, redirect: any) => {
  const state = store.getState();
  if (state.app.isLoggedIn) {
    redirect({
      pathname: '/',
      search: '',
    });
  }
};

export const requireAuth = (store: Object) => (nextState: any, redirect: any) => {
  const state = store.getState();
  if (!state.app.isLoggedIn) {
    redirect({
      pathname: '/login',
      search: '',
    });

    return;
  }

  const isRootPath = nextState.location.pathname === '/';
  if (state.app.isGrabLoggedIn && isRootPath) {
    redirect({
      pathname: '/home',
      search: '',
    });

    return;
  }

  if (!state.app.isGrabLoggedIn && !isRootPath) {
    redirect({
      pathname: '/',
      search: '',
    });
  }
};

/**
 * Pass in object and break down each object.object if it is an array
 */
export const queryStringFromObject = function queryStringFromObject(obj: Object, params: Params = {}) {
  const { formatType } = params;
  return Object.keys(obj).map(k => {
    if (Array.isArray(obj[k])) {
      switch (formatType) {
        case 'bracket':
          // returns foo[]=bar&foo[]=qux
          return obj[k].map((v) => `${encodeURIComponent(k)}[]=${encodeURIComponent(v)}`).join('&');
        case 'indexed':
          // returns foo[0]=bar&foo[1]=qux
          return obj[k].map((v, i) => `${encodeURIComponent(k)}[${i}]=${encodeURIComponent(v)}`).join('&');
        case 'comma':
          // returns foo=bar,qux
          return `${encodeURIComponent(k)}=${obj[k].map((v) => encodeURIComponent(v)).join(',')}`;
        default:
          // returns foo=bar&foo=qux
          return obj[k].map((v) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`).join('&');
      }
    }
    // returns foo=bar&qux=abc
    return `${encodeURIComponent(k)}=${encodeURIComponent(obj[k])}`;
  }).join('&');
};

export const removeUndefinedAndBlankValues = function removeUndefinedAndBlankValues(query: Object) {
  Object.keys(query).forEach((key) => {
    const value = query[key];
    if (_.isUndefined(value)) {
      delete query[key];
    }
    if (_.isString(value)) {
      const trimmedValue = value.trim();
      if (_.isEmpty(trimmedValue)) {
        delete query[key];
      }
    }
  });
  return query;
};

export const buildUrl = function buildUrl(obj: Object) {
  const { path, params } = obj;
  const refinedParams = removeUndefinedAndBlankValues(params);
  return `${path}?${queryStringFromObject(refinedParams)}`;
};

export const generateUUID = function generateUUID() {
  function s4() {
    return Math.floor((1 + Math.random()) * 0x10000)
      .toString(16)
      .substring(1);
  }
  return _.join(_.times(8, s4), '');
};

/**
 * Injects the 'view' property of the model with all of its given keys
 * @param {Model} model Model to inject views
 */
export const injectModelPropsToView = function injectModelPropsToView(model) {
  if (model && model.views) {
    const injectedViews = Object.keys(model.views).reduce(
      (accumulator, viewKey) => {
        const View = model.views[viewKey];

        accumulator[viewKey] = (props) => (
          <View {...Object.assign({}, model, props)} />
        );

        return accumulator;
      },
      {}
    );

    model.views = injectedViews;
  }

  return model;
};

/**
 * Injects the 'view' property of the model with all of its given keys
 * @param {Model} model Model to inject views
 */
export const injectOmegaConfigToView = function injectOmegaConfigToView(ViewComponent, omegaConfig) {
  return props => (
    <ViewComponent {...Object.assign({}, { omegaConfig }, props)} />
  );
};

/**
 * Returns the namespaced url based on window config variable
 * @param {String} namespace
 * @param {String} path Optional path to append to namespace
 */
export const getNamespacedUrl = function(namespace, path = '') {
  let url = `https://${window.Grab.config.BASE_URL}`;

  // Base namespace should use default BASE_URL
  if (namespace !== 'base') {
    url = url.replace('omega', `omega-${namespace}`);
  }

  if (path) {
    url = new URL(path, url).href;
  }

  return url;
};

/**
 * Generate Links from Omega Configs
 * @param {{
 *  [configName: string]: OmegaConfig,
 * }} configs
 * @return {{
 *   webPath: string,
 *   localeSelector: string,
 *   permissionKey: string,
 *   priority: number,
 * }}
 */
export const generateOmegaConfigLinks = function generateOmegaConfigLinksFunc(configs) {
  return Object.values(configs)
    .filter(({ pageConfig: { routerPath } = {} }) => !_.isEmpty(routerPath))
    .map(({ pageConfig: { routerPath, localeKey, permissionKey, sidebarPriority } }) => ({
      webPath: routerPath,
      localeSelector: localeKey,
      permissionKey,
      priority: sidebarPriority,
    }));
};
